import { capitalizeString, formatAddress, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { OpportunityStatusIndicator } from '@strata/react-status-indicators';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import { FixInvalidDataDialog } from 'components/opportunities/dialogs/FixInvalidDataDialog';
import { OpportunityDetailsOverview } from 'components/opportunities/OpportunityDetailsOverview';
import { ReferralChecklist } from 'components/referrals/ReferralChecklist';
import { AuthDetails } from 'components/shared/AuthDetails';
import { LogFillActivityDialog } from 'components/shared/dialogs/LogFillActivityDialog';
import { FillActivity } from 'components/shared/FillActivity';
import { Notes } from 'components/shared/Notes';
import { buildIntakeCaseMilestoneReferralFieldsFromTreatment } from 'services/intakeCaseHelpers';
import { useGoBack } from 'services/navigationHelpers';
import { AppRoutes } from 'services/routes';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';
import { useGetOpportunityQuery } from 'store/api/opportunityCuratorApi';
import { useGetAllProductServicesQuery, useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { useGetTreatmentDetailsQuery } from 'store/api/treatmentApi';
import { useGetOnboardingActivityQuery } from 'store/api/userSupportApi';
import { fetchReferralEvents } from 'store/referrals/referralsSlice';

const ActionDialogs = {
    FIX_INVALID_DATA: 'FIX_INVALID_DATA',
    LOG_FILL_ACTIVITY: 'LOG_FILL_ACTIVITY',
};

const ActionDialogComponentMap = {
    [ActionDialogs.FIX_INVALID_DATA]: FixInvalidDataDialog,
    [ActionDialogs.LOG_FILL_ACTIVITY]: LogFillActivityDialog,
};

const backToDestinationDefault = {
    route: AppRoutes.opportunitiesList,
    text: 'Opportunities',
};

// eslint-disable-next-line complexity
export const OpportunityDetailPage = () => {
    let { referralId } = useParams();
    referralId = decodeURIComponent(referralId);
    const location = useLocation();
    const navigate = useNavigate();

    const { backToDestination, goBack } = useGoBack(backToDestinationDefault);

    const dispatch = useDispatch();

    const { data: segments } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segments ?? {};

    const { data: products } = useGetAllProductServicesQuery();
    const { productServiceMap } = products ?? {};

    const { data: opportunityDetails, isError: opportunityError } =
        useGetOpportunityQuery(referralId);
    useEffect(() => {
        if (opportunityError) {
            goBack();
        }
    }, [goBack, opportunityError]);

    useEffect(() => {
        if (opportunityDetails?.referralId) {
            dispatch(fetchReferralEvents(opportunityDetails.referralId));
        }
    }, [dispatch, opportunityDetails?.referralId]);

    const { data: treatmentDetails, isFetching: treatmentFetching } = useGetTreatmentDetailsQuery(
        {
            payerId: opportunityDetails?.payerId,
            treatmentId: opportunityDetails?.treatmentId,
        },
        {
            skip: !opportunityDetails,
        },
    );

    const intakeMilestoneReferralId = opportunityDetails?.referralId ?? referralId;

    const intakeCaseMilestoneReferralFields = useMemo(
        () =>
            buildIntakeCaseMilestoneReferralFieldsFromTreatment(
                treatmentDetails,
                intakeMilestoneReferralId,
            ),
        [treatmentDetails, intakeMilestoneReferralId],
    );

    const { data: events, isFetching: eventsFetching } = useGetOnboardingActivityQuery({
        referralId,
    });

    const { memberDetails } = useScopeAssessorMemberDetails(opportunityDetails);

    const { action } = location.state ?? {};
    const [actionDialogToShow, setActionDialogToShow] = useState(action);
    const ActiveActionDialog = ActionDialogComponentMap[actionDialogToShow];
    const closeActionDialog = () => setActionDialogToShow();

    const menuItems = [
        {
            text: LogFillActivityDialog.Title,
            onItemClick: () => setActionDialogToShow(ActionDialogs.LOG_FILL_ACTIVITY),
        },
        ...(opportunityDetails?.canFix
            ? [
                  {
                      text: 'Fix Invalid Data',
                      onItemClick: () => setActionDialogToShow(ActionDialogs.FIX_INVALID_DATA),
                  },
              ]
            : []),
        {
            text: 'Go to Patient Treatment',
            onItemClick: () =>
                navigate(
                    AppRoutes.treatmentDetails(
                        opportunityDetails.payerId,
                        opportunityDetails.treatmentId,
                    ),
                    {
                        state: {
                            backToDestination: {
                                text: 'Opportunity Details',
                            },
                        },
                    },
                ),
        },
    ];

    return (
        <>
            {ActiveActionDialog && (
                <ActiveActionDialog
                    isOpen
                    onClose={closeActionDialog}
                    opportunityDetails={opportunityDetails}
                    memberDetails={memberDetails}
                    backToDestination={backToDestination}
                />
            )}
            <FormHeaderAndTabbedContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label={backToDestination.text}
                            title="Opportunity Details"
                            onClick={goBack}
                        />
                        {menuItems.length > 0 && (
                            <div style={{ marginLeft: 'auto' }}>
                                <ButtonWithDropdownMenu
                                    menuItems={menuItems}
                                    intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                    label="Actions"
                                />
                            </div>
                        )}
                    </>
                }
                rowThreeContentHeader={
                    opportunityDetails && (
                        <>
                            <FormTitleLabelValuePair
                                label={formatNameLastFirst(
                                    memberDetails?.lastName,
                                    memberDetails?.firstName,
                                )}
                                value={formatAddress(
                                    memberDetails?.memberCity,
                                    memberDetails?.memberState,
                                    memberDetails?.memberZip,
                                )}
                            />
                            <HeaderLabelValuePair
                                label="Product"
                                value={productServiceMap?.[opportunityDetails?.productServiceOid]}
                            />
                            <HeaderLabelValuePair
                                label="Health Plan"
                                value={
                                    payerSegmentMap?.[opportunityDetails?.segmentId]?.segment_name
                                }
                            />
                            <HeaderLabelValuePair
                                label="Benefit"
                                value={capitalizeString(opportunityDetails?.benefit)}
                            />
                            <HeaderLabelValuePair
                                label="Status"
                                value={
                                    <OpportunityStatusIndicator
                                        status={opportunityDetails?.status}
                                    />
                                }
                            />
                        </>
                    )
                }
                activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
                onTabSelect={(tab) =>
                    navigate(
                        {
                            ...location,
                            hash: tab.replace(/\s/g, '-').toLocaleLowerCase(),
                        },
                        { state: location.state, replace: true },
                    )
                }
                tabs={[
                    {
                        title: 'Overview',
                        mainContent: (
                            <OpportunityDetailsOverview
                                referralId={referralId}
                                showMemberDetails
                                intakeCaseMilestoneReferralFields={
                                    intakeCaseMilestoneReferralFields
                                }
                            />
                        ),
                    },
                    {
                        title: 'Auth Details',
                        mainContent: (
                            <AuthDetails
                                auths={treatmentDetails?.authList}
                                isContingentTherapy={treatmentDetails?.isContingentTherapy}
                                isLoading={treatmentFetching}
                            />
                        ),
                    },
                    {
                        title: 'Notes',
                        className: 'tabbed-grid-content',
                        mainContent: <Notes referralId={opportunityDetails?.referralId} />,
                    },
                    {
                        title: 'Fill Activity',
                        mainContent: <FillActivity events={events} isLoading={eventsFetching} />,
                    },
                    {
                        title: 'Raw Events',
                        mainContent: (
                            <ReferralChecklist referralId={opportunityDetails?.referralId} />
                        ),
                    },
                ]}
            />
        </>
    );
};
